// components/bus-finder/operators/OperatorDetails.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FiArrowLeft, 
  FiEdit, 
  FiPhone, 
  FiGlobe, 
  FiCheckCircle, 
  FiXCircle, 
  FiCalendar, 
  FiMapPin, 
  FiUsers,
  FiLoader,
  FiExternalLink,
  FiInfo,
  FiMap,
  FiStar,
  FiAlertCircle,
  FiNavigation
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import BaseUrl from '../../../Url/BaseUrl';

const OperatorDetails = () => {
  const { operatorNumber } = useParams();
  const navigate = useNavigate();
  const [operator, setOperator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedBuses, setRelatedBuses] = useState([]);
  const [busesLoading, setBusesLoading] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);

  useEffect(() => {
    fetchOperatorDetails();
    fetchBusesByOperator();
  }, [operatorNumber]);

  const fetchOperatorDetails = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BaseUrl.baseurl}BusOperators/${operatorNumber}`, {
        headers: { 'accept': 'text/plain' }
      });
      if (!response.ok) throw new Error('Operator not found');
      const data = await response.json();
      setOperator(data);
    } catch (error) {
      console.error('Error fetching operator details:', error);
      toast.error('Failed to load operator details');
    } finally {
      setLoading(false);
    }
  };

  const fetchBusesByOperator = async () => {
    setBusesLoading(true);
    try {
      const response = await fetch(`${BaseUrl.baseurl}Buses/BusesByOperator?OperatorNo=${operatorNumber}`, {
        headers: { 'accept': 'text/plain' }
      });
      if (response.ok) {
        const buses = await response.json();
        setRelatedBuses(buses);
      } else {
        throw new Error('Failed to fetch buses');
      }
    } catch (error) {
      console.error('Error fetching buses by operator:', error);
      toast.error('Failed to load related buses');
      setRelatedBuses([]);
    } finally {
      setBusesLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/bus-finder/operators/edit/${operatorNumber}`);
  };

  const toggleStatus = async () => {
    if (!operator) return;
    setStatusUpdating(true);
    const updatedStatus = !operator.status;
    const updatedOperator = { ...operator, status: updatedStatus };

    try {
      const response = await fetch(`${BaseUrl.baseurl}BusOperators/${operatorNumber}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'accept': 'text/plain'
        },
        body: JSON.stringify(updatedOperator)
      });

      if (response.ok) {
        setOperator(updatedOperator);
        toast.success(updatedStatus ? 'Operator activated successfully' : 'Operator deactivated successfully', {
          style: { background: '#ffffff', color: '#1f2937', border: '1px solid #e5e7eb' }
        });
      } else {
        throw new Error('Status update failed');
      }
    } catch (error) {
      console.error('Error updating operator status:', error);
      toast.error('Failed to update operator status');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleBack = () => {
    navigate('/bus-finder/operators/manage');
  };

  const getStatusBadge = (status) => {
    return status ? (
      <span className="status-badge active">
        <FiCheckCircle /> Active
      </span>
    ) : (
      <span className="status-badge inactive">
        <FiXCircle /> Inactive
      </span>
    );
  };

  const renderContactNumbers = (numbers) => {
    if (!numbers) return <p>N/A</p>;
    if (Array.isArray(numbers) && numbers.length) {
      return (
        <ul className="contact-list">
          {numbers.map((num, idx) => <li key={idx}>{num}</li>)}
        </ul>
      );
    }
    return <p>{numbers}</p>;
  };

  const renderAppLinks = () => {
    const hasAndroid = operator.appLink;
    const hasIOS = operator.iOSAppLink;
    if (!hasAndroid && !hasIOS) return <span className="no-data">No mobile apps</span>;
    return (
      <div className="app-links">
        {hasAndroid && (
          <a href={operator.appLink} target="_blank" rel="noopener noreferrer" className="link android-link">
            <FiExternalLink /> Android App
          </a>
        )}
        {hasIOS && (
          <a href={operator.iOSAppLink} target="_blank" rel="noopener noreferrer" className="link ios-link">
            <FiExternalLink /> iOS App
          </a>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="loading-container">
        <FiLoader className="spinning" size="2em" />
        <p>Loading operator details...</p>
      </div>
    );
  }

  if (!operator) {
    return (
      <div className="not-found">
        <FiAlertCircle size="3em" color="#dc3545" />
        <h3>Operator Not Found</h3>
        <p>The requested operator does not exist or has been removed.</p>
        <button onClick={handleBack} className="btn-primary">
          <FiArrowLeft /> Back to Operators
        </button>
      </div>
    );
  }

  // Helper to get state name
  const getStateName = () => {
    if (operator.state?.stateName) return operator.state.stateName;
    if (operator.stateName) return operator.stateName;
    return operator.stateCode || 'N/A';
  };

  // Helper to get zone name
  const getZoneName = () => {
    if (operator.zone?.zoneName) return operator.zone.zoneName;
    if (operator.zoneName) return operator.zoneName;
    return operator.zoneCode || 'N/A';
  };

  return (
    <div className="details-container">
      <div className="details-header">
        <button onClick={handleBack} className="btn-back">
          <FiArrowLeft /> Back to Operators
        </button>
        
        <div className="header-actions">
          <button onClick={handleEdit} className="btn-edit">
            <FiEdit /> Edit Operator
          </button>
          <button 
            onClick={toggleStatus} 
            className={`btn-status ${operator.status ? 'deactivate' : 'activate'}`}
            disabled={statusUpdating}
          >
            {statusUpdating ? (
              <><FiLoader className="spinning" /> Updating...</>
            ) : operator.status ? (
              <><FiXCircle /> Deactivate</>
            ) : (
              <><FiCheckCircle /> Activate</>
            )}
          </button>
        </div>
      </div>

      <div className="profile-card">
        <div className="profile-header">
          <div className="profile-avatar">
            <span className="avatar-text">{operator.operatorName?.charAt(0) || 'O'}</span>
          </div>
          <div className="profile-info">
            <h1>{operator.operatorName || 'Unknown Operator'}</h1>
            <div className="profile-meta">
              {getStatusBadge(operator.status)}
              <span className="operator-id">
                <FiUsers /> {operator.operatorNumber}
              </span>
             
            </div>
          </div>
        </div>

        {operator.description && (
          <div className="profile-description">
            <p>{operator.description}</p>
          </div>
        )}
      </div>

      <div className="details-grid">
        <div className="details-section">
          <h3><FiPhone /> Contact Information</h3>
          <div className="section-content">
            <div className="detail-item">
              <label>Primary Contact</label>
              {renderContactNumbers(operator.busOperatorContactNumber)}
            </div>
            <div className="detail-item">
              <label>Website</label>
              <p>
                {operator.websiteContactLink ? (
                  <a href={operator.websiteContactLink} target="_blank" rel="noopener noreferrer" className="website-link-dark">
                    <FiGlobe /> {operator.websiteContactLink.replace('https://', '').replace('http://', '')}
                  </a>
                ) : (
                  <span className="no-data">No website</span>
                )}
              </p>
            </div>
            <div className="detail-item">
              <label>Mobile Apps</label>
              {renderAppLinks()}
            </div>
          </div>
        </div>

        <div className="details-section">
          <h3><FiUsers /> Statistics</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-value">{relatedBuses.length}</div>
              <div className="stat-label">Total Buses</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">
                {relatedBuses.filter(bus => bus.status).length}
              </div>
              <div className="stat-label">Active Buses</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">
                {new Set(relatedBuses.map(bus => bus.stateCode)).size}
              </div>
              <div className="stat-label">States Covered</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">
                {new Set(relatedBuses.map(bus => bus.zoneCode)).size}
              </div>
              <div className="stat-label">Zones Covered</div>
            </div>
          </div>
        </div>

        <div className="details-section">
          <h3><FiMap /> Administrative Information</h3>
          <div className="section-content">
            <div className="detail-item">
              <label>State</label>
              <p>{getStateName()} {operator.stateCode && `(${operator.stateCode})`}</p>
            </div>
            <div className="detail-item">
              <label>Zone</label>
              <p>{getZoneName()} {operator.zoneCode && `(${operator.zoneCode})`}</p>
            </div>
          </div>
        </div>

        <div className="details-section full-width">
          <div className="section-header">
            <h3><FiUsers /> Assigned Buses ({relatedBuses.length})</h3>
            {relatedBuses.length > 0 && (
              <button onClick={fetchBusesByOperator} className="btn-refresh" disabled={busesLoading}>
                <FiLoader className={busesLoading ? 'spinning' : ''} />
                Refresh
              </button>
            )}
          </div>
          
          {busesLoading ? (
            <div className="loading-small">
              <FiLoader className="spinning" /> Loading buses...
            </div>
          ) : relatedBuses.length > 0 ? (
            <div className="buses-table-container">
              <table className="buses-table">
                <thead>
                  <tr>
                    <th>Bus Number</th>
                    <th>Registration</th>
                    <th>Bus Name</th>
                    <th>Route Path</th>
                    <th>Status</th>
                    <th>State</th>
                    <th>Zone</th>
                  </tr>
                </thead>
                <tbody>
                  {relatedBuses.map((bus) => (
                    <tr key={bus.busNo}>
                      <td>
                        <span className="bus-number">{bus.busNo}</span>
                      </td>
                      <td>
                        <span className="registration">{bus.busRegistrationNumber}</span>
                      </td>
                      <td className="bus-name">
                        <strong>{bus.busName || '—'}</strong>
                      </td>
                      <td>
                        <div className="route-info">
                          <span className="route-path">{bus.routePathNo}</span>
                          {bus.routePath?.routePathName && (
                            <span className="route-name">{bus.routePath.routePathName}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={`bus-status ${bus.status ? 'active' : 'inactive'}`}>
                          {bus.status ? (
                            <>
                              <FiCheckCircle /> Active
                            </>
                          ) : (
                            <>
                              <FiXCircle /> Inactive
                            </>
                          )}
                        </span>
                      </td>
                      <td>
                        <div className="location-info">
                          <span className="state-name">{bus.state?.stateName || bus.stateCode || 'N/A'}</span>
                          {bus.stateCode && <span className="state-code">({bus.stateCode})</span>}
                        </div>
                      </td>
                      <td>
                        <div className="location-info">
                          <span className="zone-name">{bus.zone?.zoneName || bus.zoneCode || 'N/A'}</span>
                          {bus.zoneCode && <span className="zone-code">({bus.zoneCode})</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-buses">
              <FiInfo size="1.5em" />
              <p>No buses assigned to this operator</p>
              <small className="subtext">Buses will appear here when assigned to this operator</small>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .details-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 2rem;
        }

        .details-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .btn-back {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: #6c757d;
          color: white;
          border: none;
          padding: 0.6rem 1.2rem;
          border-radius: 8px;
          cursor: pointer;
          font-size: 0.9rem;
          transition: all 0.2s;
        }

        .btn-back:hover {
          background: #5a6268;
          transform: translateY(-1px);
        }

        .header-actions {
          display: flex;
          gap: 1rem;
        }

        .btn-edit {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.6rem 1.2rem;
          border-radius: 8px;
          border: none;
          cursor: pointer;
          font-size: 0.9rem;
          font-weight: 500;
          transition: all 0.2s;
          background: linear-gradient(135deg, #FFFDE7 0%, #FFF9C4 100%);
          color: black;
        }

        .btn-edit:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .btn-status {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.6rem 1.2rem;
          border-radius: 8px;
          border: none;
          cursor: pointer;
          font-size: 0.9rem;
          font-weight: 500;
          transition: all 0.2s;
        }

        .btn-status.activate {
          background: #d4edda;
          color: #155724;
        }

        .btn-status.deactivate {
          background: #f8d7da;
          color: #721c24;
        }

        .btn-status:hover:not(:disabled) {
          transform: translateY(-1px);
          filter: brightness(0.95);
        }

        .btn-status:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .profile-card {
          background: linear-gradient(135deg, #dced9b 0%, #4aa5eb 100%);
          border-radius: 16px;
          padding: 2rem;
          color: black;
          margin-bottom: 2rem;
          box-shadow: 0 8px 32px rgba(102, 126, 234, 0.2);
        }

        .profile-header {
          display: flex;
          align-items: center;
          gap: 1.5rem;
          margin-bottom: 1.5rem;
        }

        .profile-avatar {
          width: 80px;
          height: 80px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          backdrop-filter: blur(10px);
          border: 3px solid rgba(255, 255, 255, 0.3);
          flex-shrink: 0;
        }

        .avatar-text {
          font-size: 2rem;
          font-weight: bold;
        }

        .profile-info h1 {
          margin: 0 0 0.5rem 0;
          font-size: 2.2rem;
          font-weight: 600;
        }

        .profile-meta {
          display: flex;
          align-items: center;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .status-badge {
          display: flex;
          align-items: center;
          gap: 0.3rem;
          padding: 0.4rem 1rem;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 500;
          background: rgba(54, 238, 158, 0.2);
          backdrop-filter: blur(10px);
        }

        .status-badge.active {
          background: green;
        }

        .status-badge.inactive {
          background: rgba(244, 67, 54, 0.3);
        }

        .operator-id, .registered-date {
          display: flex;
          align-items: center;
          gap: 0.3rem;
          font-size: 0.9rem;
          opacity: 0.9;
          background: rgba(255, 255, 255, 0.1);
          padding: 0.4rem 0.8rem;
          border-radius: 20px;
        }

        .profile-description {
          font-size: 1.1rem;
          line-height: 1.6;
          opacity: 0.95;
          max-width: 800px;
        }

        .details-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 1.5rem;
        }

        .details-section {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
        }

        .details-section.full-width {
          grid-column: 1 / -1;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
        }

        .details-section h3 {
          margin: 0;
          color: #333;
          font-size: 1.2rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding-bottom: 0.75rem;
          border-bottom: 2px solid #f0f0f0;
        }

        .btn-refresh {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: #f8f9fa;
          border: 1px solid #dee2e6;
          padding: 0.4rem 0.8rem;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.85rem;
          color: #495057;
          transition: all 0.2s;
        }

        .btn-refresh:hover:not(:disabled) {
          background: #e9ecef;
          border-color: #ced4da;
        }

        .btn-refresh:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .section-content {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }

        .detail-item {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .detail-item label {
          font-size: 0.85rem;
          color: #666;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .detail-item p {
          margin: 0;
          color: #333;
          font-size: 1rem;
        }

        .contact-list {
          margin: 0;
          padding-left: 1.25rem;
          color: #333;
        }

        .contact-list li {
          margin-bottom: 0.25rem;
        }

        .website-link-dark {
          color: #1976d2;
          text-decoration: none;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: color 0.2s;
        }

        .website-link-dark:hover {
          text-decoration: underline;
          color: #0b5e9e;
        }

        .app-links {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .android-link, .ios-link {
          color: #1976d2;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.9rem;
        }

        .android-link:hover, .ios-link:hover {
          text-decoration: underline;
        }

        .no-data {
          color: #999;
          font-style: italic;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
        }

        .stat-item {
          text-align: center;
          padding: 1rem;
          background: #f8f9fa;
          border-radius: 8px;
          transition: transform 0.2s;
        }

        .stat-item:hover {
          transform: translateY(-2px);
          background: #f0f0f0;
        }

        .stat-value {
          font-size: 2rem;
          font-weight: bold;
          color: black;
          margin-bottom: 0.25rem;
        }

        .stat-label {
          font-size: 0.9rem;
          color: #666;
        }

        .buses-table-container {
          overflow-x: auto;
          margin-top: 1rem;
        }

        .buses-table {
          width: 100%;
          border-collapse: collapse;
        }

        .buses-table th {
          background: #f8f9fa;
          padding: 1rem;
          text-align: left;
          font-weight: 600;
          color: #495057;
          border-bottom: 2px solid #dee2e6;
          font-size: 0.9rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .buses-table td {
          padding: 1rem;
          border-bottom: 1px solid #eee;
          color: #212529;
          vertical-align: middle;
        }

        .buses-table tr:hover {
          background: #f8fafc;
        }

        .bus-number {
          background: #e3f2fd;
          color: #1976d2;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-weight: 600;
          font-family: 'Monaco', 'Courier New', monospace;
        }

        .registration {
          font-family: 'Monaco', 'Courier New', monospace;
          font-size: 0.9rem;
        }

        .bus-name {
          font-weight: 600;
        }

        .route-info {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .route-path {
          background: #e8f5e9;
          color: #2e7d32;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-weight: 600;
          font-family: 'Monaco', 'Courier New', monospace;
          font-size: 0.85rem;
          display: inline-block;
          width: fit-content;
        }

        .route-name {
          font-size: 0.85rem;
          color: #666;
          font-style: italic;
        }

        .bus-status {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.35rem 0.75rem;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 500;
          width: fit-content;
        }

        .bus-status.active {
          background: #d4edda;
          color: #155724;
        }

        .bus-status.inactive {
          background: #f8d7da;
          color: #721c24;
        }

        .location-info {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .state-name, .zone-name {
          font-size: 0.9rem;
          font-weight: 500;
          color: #333;
        }

        .state-code, .zone-code {
          font-size: 0.8rem;
          color: #666;
        }

        .no-buses {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 3rem;
          color: #666;
          gap: 0.75rem;
          text-align: center;
        }

        .no-buses p {
          margin: 0;
          font-size: 1.1rem;
        }

        .subtext {
          color: #999;
          font-size: 0.9rem;
          max-width: 300px;
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 400px;
          gap: 1rem;
        }

        .loading-small {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          color: #666;
          padding: 2rem;
        }

        .spinning {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .not-found {
          text-align: center;
          padding: 4rem 2rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
        }

        .not-found h3 {
          color: #333;
          margin: 0;
          font-size: 1.5rem;
        }

        .not-found p {
          color: #666;
          margin: 0;
          max-width: 400px;
        }

        .btn-primary {
          background: linear-gradient(135deg, #FFFDE7 0%, #FFF9C4 100%);
          color: black;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-top: 1rem;
          transition: all 0.2s;
        }

        .btn-primary:hover {
          opacity: 0.9;
          transform: translateY(-1px);
        }

        @media (max-width: 768px) {
          .details-container {
            padding: 1rem;
          }

          .details-header {
            flex-direction: column;
            align-items: stretch;
          }

          .header-actions {
            flex-direction: column;
          }

          .btn-edit, .btn-status {
            justify-content: center;
          }

          .profile-header {
            flex-direction: column;
            text-align: center;
          }

          .profile-meta {
            justify-content: center;
          }

          .details-grid {
            grid-template-columns: 1fr;
          }

          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .section-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;
          }

          .btn-refresh {
            align-self: flex-start;
          }
        }

        @media (max-width: 480px) {
          .stats-grid {
            grid-template-columns: 1fr;
          }

          .buses-table {
            font-size: 0.85rem;
          }

          .buses-table th,
          .buses-table td {
            padding: 0.75rem;
          }
        }
      `}</style>
    </div>
  );
};

export default OperatorDetails;